import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import { evaluationApi } from "../../api/performanceApi";
import {
  useEvaluation,
  useSaveEvaluationResponses,
  useSubmitEvaluation,
} from "../../api/queries/performanceQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import { formatDateTime, formatPlainDate } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import EvaluationStatusBadge from "./components/EvaluationStatusBadge";
import EvaluationForm, { cleanResponses } from "./components/EvaluationForm";
import { ActivityTimeline, ScorePanel } from "./components/EvaluationPanels";
import { useEvaluationDialogs } from "./components/EvaluationDialogs";
import {
  errorList,
  isOverdue,
  latestReturn,
} from "./components/evaluationUtils";
import "./performance.css";
import { useCanModify } from "../../hooks/usePermissions";

const AUTOSAVE_DELAY = 1200;
const SAVE_TEXT = {
  pending: "Unsaved changes…",
  saving: "Saving…",
  saved: "All changes saved",
  error: "Not saved",
};

const Meta = ({ label, children }) => (
  <div>
    <div className="approval-meta-label">{label}</div>
    <div className="eval-meta-value">{children || "—"}</div>
  </div>
);

/**
 * One evaluation. Buttons come only from `permissions`.
 * ?edit=1 turns on HR encoding (when canEditEvaluation): changes autosave
 * (the whole responses object each time) and the score updates from the reply.
 */
const EvaluationDetailPage = () => {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const query = useEvaluation(id);
  const ev = query.data;
  // Server permissions per evaluation; view-only roles (no performance modify) get none
  const canModify = useCanModify("performance");
  const p = canModify ? ev?.permissions || {} : {};
  const { open, dialog } = useEvaluationDialogs();

  const save = useSaveEvaluationResponses();
  const submit = useSubmitEvaluation();
  const editing = params.get("edit") === "1" && !!p.canEditEvaluation;

  // ---- Encoding state ----
  const [draft, setDraft] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [errors, setErrors] = useState([]);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const draftRef = useRef(null);
  const pendingRef = useRef(false);
  const timerRef = useRef(null);

  // Start from the server's answers when editing begins; drop the draft when it ends
  if (editing && draft === null && ev) {
    setDraft(ev.responses || {});
    draftRef.current = ev.responses || {};
  } else if (!editing && draft !== null) {
    setDraft(null);
  }

  const doSave = async (responses) => {
    setSaveState("saving");
    try {
      await save.mutateAsync({ id, responses: cleanResponses(responses) });
      if (draftRef.current === responses) {
        pendingRef.current = false;
        setSaveState("saved");
      }
      setErrors([]);
      return true;
    } catch (err) {
      if (getApiErrorStatus(err) === 409 || getApiErrorStatus(err) === 403) {
        // Status changed under us: reload what the server has
        toast.info(
          getApiErrorMessage(err, "Evaluation was changed by someone else."),
        );
        pendingRef.current = false;
        setDraft(null);
        setSaveState("idle");
      } else {
        setErrors(errorList(err, "Could not save."));
        setSaveState("error");
      }
      return false;
    }
  };

  const onChange = (next) => {
    setDraft(next);
    draftRef.current = next;
    pendingRef.current = true;
    setSaveState("pending");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => doSave(draftRef.current),
      AUTOSAVE_DELAY,
    );
  };

  const flush = async () => {
    clearTimeout(timerRef.current);
    return pendingRef.current ? doSave(draftRef.current) : true;
  };

  // Leaving the page with unsaved changes: send them anyway
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingRef.current && draftRef.current) {
        evaluationApi
          .saveResponses(id, cleanResponses(draftRef.current))
          .catch(() => {});
      }
    },
    [id],
  );

  useEffect(() => {
    if (saveState !== "pending" && saveState !== "saving") return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saveState]);

  const setEditing = async (on) => {
    if (!on && !(await flush())) return;
    const next = new URLSearchParams(params);
    on ? next.set("edit", "1") : next.delete("edit");
    setParams(next, { replace: true });
  };

  const doSubmit = async () => {
    setConfirmSubmit(false);
    if (!(await flush())) return;
    try {
      await submit.mutateAsync(id);
      toast.success("Evaluation submitted.");
      setErrors([]);
      setEditing(false);
    } catch (err) {
      const status = getApiErrorStatus(err);
      if (status === 409 || status === 403)
        toast.error(getApiErrorMessage(err, "Could not submit."));
      else setErrors(errorList(err, "Could not submit."));
    }
  };

  if (query.isLoading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      </Layout>
    );
  }
  if (query.isError || !ev) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="alert alert-danger" role="alert">
            {getApiErrorMessage(query.error, "Could not load this evaluation.")}{" "}
            <Link to="/performance/evaluations">Back to evaluations</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const returned =
    ev.status === "PENDING_EVALUATION" ? latestReturn(ev.activities) : null;
  const overdue = isOverdue(ev);

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        {/* Header */}
        <div className="builder-header">
          <div className="builder-header-text">
            <Link
              to="/performance/evaluations"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Evaluations
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title text-truncate">
              {employeeName(ev.employee)}
            </h1>
            <div className="d-flex flex-wrap align-items-center gap-2 fs-080 text-slate-500 mt-1">
              <span>{ev.employee?.employeeId}</span>
              {ev.employee?.branch?.branchName && (
                <span>· {ev.employee.branch.branchName}</span>
              )}
              <EvaluationStatusBadge evaluation={ev} />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Link
              to={`/performance/evaluations/${ev.id}/print`}
              target="_blank"
              rel="noopener"
              className="btn profile-btn profile-btn-white"
            >
              <i className="bi bi-printer"></i> Print
            </Link>
            {p.canChangeEvaluator && (
              <button
                type="button"
                className="btn profile-btn profile-btn-white"
                onClick={() => open("reassign", ev)}
              >
                <i className="bi bi-arrow-left-right"></i> Reassign evaluator
              </button>
            )}
            {p.canCancel && (
              <button
                type="button"
                className="btn btn-outline-danger profile-btn bg-white"
                onClick={() => open("cancel", ev)}
              >
                <i className="bi bi-x-circle"></i> Cancel
              </button>
            )}
            {p.canResolveContest && (
              <button
                type="button"
                className="btn btn-warning profile-btn"
                onClick={() => open("resolve", ev)}
              >
                <i className="bi bi-exclamation-octagon"></i> Resolve contest
              </button>
            )}
            {p.canEditEvaluation && !editing && (
              <button
                type="button"
                className="btn btn-primary profile-btn"
                onClick={() => setEditing(true)}
              >
                <i className="bi bi-pencil-square"></i> Encode ratings
              </button>
            )}
          </div>
        </div>

        {ev.employeeResponse && (
          <div
            className={`alert ${ev.employeeResponse === "CONTEST" ? "alert-warning" : "alert-success"} d-flex gap-2 fs-085`}
            role="status"
          >
            <i
              className={`bi ${ev.employeeResponse === "CONTEST" ? "bi-exclamation-octagon-fill" : "bi-hand-thumbs-up-fill"} mt-1`}
            ></i>
            <div>
              <strong>
                {ev.employeeResponse === "CONTEST"
                  ? "The employee contested this evaluation"
                  : "The employee accepted this evaluation"}
              </strong>
              {ev.acknowledgedAt && (
                <span className="text-slate-600">
                  {" "}
                  · {formatDateTime(ev.acknowledgedAt)}
                </span>
              )}
              {ev.employeeComment && (
                <div className="white-space-pre mt-1">
                  “{ev.employeeComment}”
                </div>
              )}
              {ev.contestResolution && (
                <div className="mt-1 fs-080">
                  Resolved:{" "}
                  <strong>
                    {ev.contestResolution === "UPHOLD"
                      ? "Result upheld"
                      : "Re-evaluation"}
                  </strong>
                  {ev.contestResolvedAt
                    ? ` · ${formatDateTime(ev.contestResolvedAt)}`
                    : ""}
                </div>
              )}
            </div>
          </div>
        )}

        {returned && (
          <div className="alert alert-danger d-flex gap-2 fs-085" role="alert">
            <i className="bi bi-arrow-return-left mt-1"></i>
            <div>
              <strong>
                {returned.action === "CONTEST_REEVALUATE"
                  ? "Sent back for re-evaluation"
                  : "Returned"}{" "}
                by {returned.actorName || "HR"}
              </strong>
              <span className="text-slate-600">
                {" "}
                · {formatDateTime(returned.createdAt)}
              </span>
              <div className="white-space-pre mt-1">{returned.remarks}</div>
            </div>
          </div>
        )}

        {editing && (
          <div className="eval-edit-bar" role="status">
            <div className="d-flex align-items-center gap-2 fs-085">
              <i className="bi bi-pencil-square text-primary"></i>
              <strong>Encoding ratings</strong>
              <span className={`eval-save-state eval-save-state--${saveState}`}>
                {SAVE_TEXT[saveState] || ""}
              </span>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary bg-white"
                onClick={() => setEditing(false)}
                disabled={submit.isPending}
              >
                Done
              </button>
              {p.canSubmit && (
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => setConfirmSubmit(true)}
                  disabled={submit.isPending}
                >
                  {submit.isPending && (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      aria-hidden="true"
                    ></span>
                  )}
                  Submit
                </button>
              )}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="alert alert-danger fs-085" role="alert">
            <div className="fw-semibold mb-1">
              <i className="bi bi-exclamation-octagon me-1"></i>
              {errors.length === 1
                ? "Please fix this:"
                : `Please fix these ${errors.length} problems:`}
            </div>
            <ul className="mb-0 ps-3">
              {errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="eval-layout">
          <div className="min-w-0">
            <EvaluationForm
              sections={ev.sections}
              responses={editing ? draft : ev.responses}
              selfResponses={ev.selfResponses}
              scores={ev.scores}
              editable={editing}
              onChange={onChange}
            />
          </div>

          <aside className="eval-side">
            <ScorePanel scores={ev.scores} saving={save.isPending} />
            <div className="eval-card eval-meta-grid">
              <Meta label="Form">
                {ev.template?.name}{" "}
                <span className="text-slate-500 fw-normal">
                  v{ev.template?.version}
                </span>
              </Meta>
              <Meta label="Period">
                {ev.periodLabel}
                <div className="text-slate-500 fw-normal fs-075">
                  {formatPlainDate(ev.periodStart)} –{" "}
                  {formatPlainDate(ev.periodEnd)}
                </div>
              </Meta>
              <Meta label="Evaluator">{employeeName(ev.evaluator)}</Meta>
              <Meta label="Due">
                <span className={overdue ? "text-danger" : ""}>
                  {ev.dueDate ? formatPlainDate(ev.dueDate) : "—"}
                  {overdue && " · overdue"}
                </span>
              </Meta>
              <Meta label="Created">{formatDateTime(ev.createdAt)}</Meta>
              <Meta label="Submitted">
                {ev.submittedAt ? formatDateTime(ev.submittedAt) : "—"}
              </Meta>
            </div>
            <ActivityTimeline activities={ev.activities} />
          </aside>
        </div>
      </div>

      {confirmSubmit && (
        <Dialog
          title="Submit and send to the employee?"
          onClose={() => setConfirmSubmit(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setConfirmSubmit(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="btn btn-success profile-btn"
                onClick={doSubmit}
              >
                Submit
              </button>
            </>
          }
        >
          <p className="fs-085 mb-2">
            Submit and send to the employee to agree or contest. Ratings can't
            be changed after this unless HR sends it back for re-evaluation.
          </p>
          {ev.scores && !ev.scores.complete && (
            <p className="fs-080 text-danger mb-0">
              Some items aren't rated yet; every rating item is required.
            </p>
          )}
        </Dialog>
      )}
      {dialog}
    </Layout>
  );
};

export default EvaluationDetailPage;
